import { Box, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { ResourceLinkGroup, resourceLinkGroups } from '../data/resourcesData';

// Create motion components
const MotionBox = motion(Box);

/**
 * A card listing one county's links
 */
function CountyCard({ county, links }: ResourceLinkGroup) {
	return (
		<Flex
			flexDirection={"column"}
			gap={4}
			height={"100%"}
			padding={6}
			backgroundColor={"blue.400"}
			borderRadius={"xl"}
			boxShadow={"lg"}
		>
			<Text
				fontFamily={"Martel"}
				fontWeight={"700"}
				fontSize={"xl"}
				color={"white.500"}
			>
				{county}
			</Text>

			<Flex as={"ul"} flexDirection={"column"} gap={3} listStyleType={"none"}>
				{links.map((link) => (
					<Box as={"li"} key={link.url}>
						<Link
							href={link.url}
							isExternal
							// Full-width row so the icon sits at the right edge of the card
							display={"flex"}
							justifyContent={"space-between"}
							alignItems={"flex-start"}
							gap={3}
							fontFamily={"Montserrat"}
							fontWeight={500}
							color={"white.500"}
							transition={"color 0.2s ease"}
							_hover={{ textDecoration: "none", color: "yellow.100" }}
						>
							{link.name}
							<Icon as={ExternalLink} boxSize={4} marginTop={1} flexShrink={0} color={"yellow.100"} />
						</Link>
					</Box>
				))}
			</Flex>
		</Flex>
	);
}

/**
 * Helpful court, clerk, sheriff, and jail links, grouped by county
 */
export default function ResourceLinks() {
	return (
		<Flex
			width={"100%"}
			flexDirection={"column"}
			alignItems={"center"}
			gap={8}
			paddingY={10}
		>
			{/* Header */}
			<SectionHeader
				size={"sm"}
				title={"Helpful Links"}
				subtitle={"Court, clerk, sheriff, and jail resources for Houston-area counties."}
			/>

			{/* County Cards: a wrapping row so a partial last row is centered */}
			<Flex
				flexWrap={"wrap"}
				justifyContent={"center"}
				gap={6}
				width={"90%"}
				maxWidth={"1200px"}
			>
				{resourceLinkGroups.map((group, index) => (
					<MotionBox
						key={group.county}
						// 1 / 2 / 3 per row, accounting for the 24px gaps
						width={{ base: "100%", md: "calc((100% - 24px) / 2)", lg: "calc((100% - 48px) / 3)" }}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.2 }}
						transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
					>
						<CountyCard {...group} />
					</MotionBox>
				))}
			</Flex>
		</Flex>
	);
}
